import React from 'react'
import { Stack, Tooltip, Typography } from '@mui/material'
import { InfoOutlined, WarningAmberRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * Presentational indicator for CU4 (stock contable vs. mínimo) used by the Part Reference table.
 *
 * S17 (CA4): always shows the accounting stock next to the minimum-stock threshold, and marks the
 * reference with a visible alert icon whenever `belowOrAtMinimum` is true (explanatory text on
 * hover via tooltip — the cell is too narrow for a label).
 *
 * S18 (CA6/D9): when `settingEnabled` is `false` (global auto-replenishment setting OFF) the
 * alert icon emphasizes that automatic replenishment is disabled and manual action is required
 * (warning tone); when `true`, the same alert surface is informational only (info tone). This is
 * also the surface used for the D2/D3 impediment alert (generic message, no distinct cause —
 * GATE 2 decision), even though this component only receives the CU4 fields.
 *
 * @param {{status?: {stockContable: number, minimumStock: number, belowOrAtMinimum: boolean, settingEnabled: boolean}}} props
 */
const MinimumStockIndicator = ({ status }) => {
  const { t_ } = useSmartTranslation()

  if (!status) {
    // Still resolving the batch call (or the reference is not controlled) — render nothing.
    return null
  }

  const { stockContable, minimumStock, belowOrAtMinimum, settingEnabled } = status

  const alertLabel = settingEnabled
    ? t_('Minimum stock reached')
    : t_('Automatic replenishment disabled — manual action required')

  return (
    // Minimal by design: the stock fraction plus a small state icon on the same line. The
    // explanatory text is only revealed on hover (tooltip) — the cell is too narrow for any label
    // without wrapping or crowding the table. Color is reserved for the setting-OFF emphasis
    // (S18): the informational case stays neutral (muted icon, default text).
    <Stack direction='row' spacing={0.75} alignItems='center'>
      <Typography
        variant='body2'
        color={belowOrAtMinimum && !settingEnabled ? 'warning.main' : 'text.primary'}
        sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}
      >
        {`${stockContable} / ${minimumStock}`}
      </Typography>
      {belowOrAtMinimum && (
        <Tooltip title={alertLabel} arrow>
          {settingEnabled
            ? <InfoOutlined fontSize='small' color='action' aria-label={alertLabel} data-testid='minimum-stock-alert-chip' />
            : <WarningAmberRounded fontSize='small' color='warning' aria-label={alertLabel} data-testid='minimum-stock-alert-chip' />}
        </Tooltip>
      )}
    </Stack>
  )
}

export default MinimumStockIndicator
